// src/pages/ShopConfigPage.js
import React, { useState, useEffect, useCallback } from "react";
import {
  fetchShopConfigs,
  fetchShopConfigByTemplate,
  addShopConfig,
  updateShopConfig,
  deleteShopConfig,
} from "../services/api";
import { useAppContext } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Pagination } from "../components/ui/pagination";
import { Input } from "../components/ui/input";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
} from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { Plus, Search } from "lucide-react";

import ShopConfigList from "../components/ShopConfig/ShopConfigList";
import ShopConfigForm from "../components/ShopConfig/ShopConfigForm";

function ShopConfigPage() {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedConfig, setSelectedConfig] = useState(null);
  const [isFormModalOpen, setIsFormModalOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [configToDelete, setConfigToDelete] = useState(null);
  const { actions } = useAppContext();

  // States to prevent multiple API calls
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [apiHasFailed, setApiHasFailed] = useState(false);

  const loadConfigs = useCallback(async () => {
    if (isLoadingData || apiHasFailed) return;

    try {
      setIsLoadingData(true);
      setLoading(true);

      const response = await fetchShopConfigs(page, 10, searchTerm);

      // Handle pagination response
      if (Array.isArray(response)) {
        setConfigs(response);
        setTotalPages(Math.ceil(response.length / 10) || 1);
      } else {
        setConfigs(response.items || response);
        setTotalPages(response.totalPages || 1);
      }

      setLoading(false);
      setApiHasFailed(false);
    } catch (err) {
      setError(err.message || "Failed to fetch shop configurations");
      setLoading(false);
      setApiHasFailed(true);
      actions.setNotification(
        err.message || "Failed to fetch shop configurations",
        "error"
      );

      // Fallback to mock data for demo purposes
      setConfigs(getMockConfigs());
    } finally {
      setIsLoadingData(false);
    }
  }, [page, searchTerm, actions, isLoadingData, apiHasFailed]);

  useEffect(() => {
    loadConfigs();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    loadConfigs();
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
  };

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleAddClick = () => {
    setSelectedConfig(null);
    setIsFormModalOpen(true);
  };

  const handleEditClick = async (config) => {
    try {
      // Fetch detailed config if necessary
      const detailedConfig = await fetchShopConfigByTemplate(
        config.templateName
      );
      setSelectedConfig(detailedConfig);
      setIsFormModalOpen(true);
    } catch (err) {
      actions.setNotification(
        err.message || "Failed to fetch configuration details",
        "error"
      );
    }
  };

  const handleDeleteClick = (config) => {
    setConfigToDelete(config);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await deleteShopConfig(configToDelete.templateName);
      setIsDeleteDialogOpen(false);
      loadConfigs();
      actions.setNotification("Configuration deleted successfully", "success");
    } catch (err) {
      actions.setNotification(
        err.message || "Failed to delete configuration",
        "error"
      );
    }
  };

  const handleFormSubmit = async (configData) => {
    try {
      if (selectedConfig) {
        await updateShopConfig(selectedConfig.templateName, configData);
        actions.setNotification(
          "Configuration updated successfully",
          "success"
        );
      } else {
        await addShopConfig(configData);
        actions.setNotification("Configuration added successfully", "success");
      }
      setIsFormModalOpen(false);
      loadConfigs();
    } catch (err) {
      actions.setNotification(
        err.message || "Failed to save configuration",
        "error"
      );
    }
  };

  // Mock data for testing or fallback
  const getMockConfigs = () => {
    return [
      {
        templateName: "coffee_shop",
        shopName: "Coffee Haven",
        paymentGateway: "CCAVENUE",
        ccavenueMerchantId: "123456",
        ccavenueWorkingKey: "WORKING_KEY_1",
        ccavenueAccessCode: "ACCESS_CODE_1",
        merchantEmail: "coffee@example.com",
        isActive: true,
      },
      {
        templateName: "book_store",
        shopName: "Book Palace",
        paymentGateway: "EASEBUZZ",
        easebuzzKey: "EASEBUZZ_KEY_1",
        easebuzzSalt: "EASEBUZZ_SALT_1",
        easebuzzUrl: "https://pay.easebuzz.in/",
        easebuzzDashboardUrl: "https://dashboard.easebuzz.in/",
        paymentMode: "test",
        merchantEmail: "books@example.com",
        isActive: true,
      },
    ];
  };

  // Render loading skeletons
  if (loading && configs.length === 0) {
    return (
      <div className="space-y-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Shop Payment Configurations</CardTitle>
            <Skeleton className="h-10 w-28" />
          </CardHeader>
          <CardContent>
            <div className="flex justify-between mb-6">
              <Skeleton className="h-10 w-64" />
              <Skeleton className="h-10 w-32" />
            </div>

            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="overflow-hidden">
                  <CardContent className="p-0">
                    <div className="grid grid-cols-4 divide-x divide-gray-200">
                      <div className="col-span-3 p-4">
                        <div className="flex justify-between">
                          <Skeleton className="h-6 w-40" />
                          <Skeleton className="h-6 w-16" />
                        </div>
                        <Skeleton className="h-4 w-32 mt-1" />
                        <div className="grid grid-cols-2 gap-4 mt-4">
                          <div>
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-4 w-24 mt-1" />
                          </div>
                          <div>
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-4 w-24 mt-1" />
                          </div>
                        </div>
                      </div>
                      <div className="p-4 flex flex-col items-center justify-center space-y-2">
                        <Skeleton className="h-9 w-20" />
                        <Skeleton className="h-9 w-20" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle>Shop Payment Configurations</CardTitle>
          <Button
            onClick={handleAddClick}
            className="bg-primary hover:bg-primary/90"
          >
            <Plus className="h-4 w-4 mr-2" /> Add Configuration
          </Button>
        </CardHeader>
        <CardContent>
          {error && (
            <div className="p-4 mb-4 text-red-700 bg-red-100 rounded-md">
              {error}
              <p className="mt-2 text-sm">Using demo data for preview</p>
              <div className="mt-2">
                <Button
                  onClick={() => {
                    setApiHasFailed(false);
                    loadConfigs();
                  }}
                  variant="outline"
                  size="sm"
                  className="text-red-600 border-red-300 hover:bg-red-50"
                >
                  Retry Connection
                </Button>
              </div>
            </div>
          )}

          <form onSubmit={handleSearch} className="flex space-x-2 mb-6">
            <div className="relative flex-grow">
              <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <Input
                type="text"
                placeholder="Search configurations..."
                value={searchTerm}
                onChange={handleSearchTermChange}
                className="pl-10 w-full"
              />
            </div>
            <Button type="submit">Search</Button>
          </form>

          <ShopConfigList
            configs={configs}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
          />

          {totalPages > 1 && (
            <div className="mt-6 flex justify-center">
              <Pagination
                currentPage={page}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add/Edit Configuration Modal */}
      <AlertDialog open={isFormModalOpen} onOpenChange={setIsFormModalOpen}>
        <AlertDialogContent size="full" className="md:max-w-5xl lg:max-w-6xl">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {selectedConfig ? "Edit Configuration" : "Add New Configuration"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {selectedConfig
                ? "Update your shop payment configuration details below."
                : "Enter details to add a new shop payment configuration."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <div className="px-6 py-4">
            <ShopConfigForm
              initialData={selectedConfig}
              onSubmit={handleFormSubmit}
              onCancel={() => setIsFormModalOpen(false)}
            />
          </div>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete the configuration for "
              {configToDelete?.shopName}". This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export default ShopConfigPage;
